// order-form action(주문 제출)의 순수 로직 모음. bunja/action.tsx(submitOrder)가 소비한다.
// socket/state를 import하지 않아 dep-free로 유닛 테스트된다(action-utils.test.ts). 새 action 순수함수는 여기에 모은다.
import { OrderTypeEnum } from '@flipster/apis/types';
import type { Timestamp } from '@flipster/apis/types';
import type {
  PostOneWayConditionalOrderParams,
  PostOneWayOrderParams,
  PostOrderParams,
  PostPositionParams,
  TriggerPriceType,
} from '@flipster/apis/v2/trade/types';
import Num from '@flipster/utils/num';
import type { OrderMarginType, V2OrderSideType } from '@flipster/utils/types/base';

import { resolveTpSlExecutionFields, type AdvancedTpSlPreset, type BasicTpSlInput } from '../../../advanced-tpsl';
import type { OrderState } from '../../form';
import { buildSlippageRequestFields, type SlippageState } from '../../slippage';

/** BBO 실행 전략 값(counterparty book 최우선). price가 null일 때만 함께 전송된다. */
const PRICE_MATCH_STRATEGY_BBO = 'PRICE_MATCH_STRATEGY_COUNTERPARTY_1';

/** 최근 bbo_price_timestamp 참조값. submitOrder에서 `refDetailTimes[symbol]`로 주입된다. */
export interface RefTimestamp {
  refServerTimestamp?: Timestamp;
  refClientTimestamp?: Timestamp;
}

/**
 * order 빌더 공통 입력 계약.
 *
 * 폼/소켓 atom을 직접 read하지 않고 모든 상태를 명시 파라미터로 받는다(dep-free 유지 — 이 모듈은
 * socket/state 파일을 import하지 않으므로 유닛 테스트가 생성파일 없이도 로드된다).
 * `requestId`/`timestamp`/`refTimestamp`는 비결정 입력이라 테스트에서 고정값을 주입해 스냅샷을 안정화한다.
 */
export interface BuildOrderRequestInput {
  // ── payload (validationBunja.payloadAtom 파생) ──
  side: V2OrderSideType;
  priceType: 'market' | 'trigger' | 'limit';
  /** trigger 발동 후 체결 방식. priceType==='trigger'일 때만 의미 있음. (Trigger Time은 sunset — ERD-216 §11) */
  triggerExecutionType: 'market' | 'limit';
  triggerPrice: string;
  triggerPriceType: TriggerPriceType;
  triggerReferencePrice: string;
  kind: OrderState['kind'];
  leverage: number;
  amount?: string;
  qty: string;
  reduceOnly: boolean;
  referencePrice: string;
  limitPrice: string;
  isBbo: boolean;
  tpSl: BasicTpSlInput;
  advancedTpSl: AdvancedTpSlPreset | null;
  // ── 외부/파생 컨텍스트 ──
  tradeMode: 'MultiplePositions' | 'OneWay';
  hasOneWayPosition: boolean;
  marginType: OrderMarginType;
  attribution?: string;
  /** market 실행가 산출용 best ask/bid (getDetailAtom). 없으면 referencePrice로 폴백. */
  askPrice?: string;
  bidPrice?: string;
  slippage: SlippageState;
  // ── 비결정 입력 (테스트 고정값 주입) ──
  requestId: string;
  timestamp: Timestamp;
  refTimestamp?: RefTimestamp;
}

/**
 * 주문 변형(priceType × 포지션 모드)과 그에 대응하는 요청 본문의 discriminated union.
 * `variant`가 어떤 주문인지 판별하고, submitOrder가 variant로 실제 API를 매핑한다. 본문 조립은 이 순수 빌더가 담당한다.
 * (market/limit × Multi는 같은 postPositionApi로 나가지만, 변형을 명확히 구분하려고 별도 variant로 둔다.)
 */
export type OrderRequestPlan =
  | { variant: 'marketMulti'; body: PostPositionParams } // postPositionApi
  | { variant: 'marketOneway'; body: PostOneWayOrderParams } // postOneWayOrderApi
  | { variant: 'limitMulti'; body: PostPositionParams } // postPositionApi
  | { variant: 'limitOneway'; body: PostOneWayOrderParams } // postOneWayOrderApi
  | { variant: 'triggerMulti'; body: PostOrderParams } // postOrderApi
  | { variant: 'triggerOneway'; body: PostOneWayConditionalOrderParams }; // postOneWayConditionalOrderApi

/** Market 주문 결과 — Multi 또는 OneWay. */
type MarketOrderPlan = Extract<OrderRequestPlan, { variant: 'marketMulti' | 'marketOneway' }>;
/** Limit 주문 결과 — Multi 또는 OneWay. */
type LimitOrderPlan = Extract<OrderRequestPlan, { variant: 'limitMulti' | 'limitOneway' }>;
/** Trigger 주문 결과 — Multi 또는 OneWay. */
type TriggerOrderPlan = Extract<OrderRequestPlan, { variant: 'triggerMulti' | 'triggerOneway' }>;

/**
 * 모든 priceType이 공유하는 요청 본문 파생값(순수).
 * - effectiveLeverage/effectiveMarginType: OneWay 포지션 보유 시 undefined(서버가 기존 포지션 값 유지)
 * - orderTypeValue: qty(quantity) | amount(margin·positionSize는 이미 amount로 환산됨)
 * - tpSlValues: advanced preset 우선, 아니면 인라인 간편 TP/SL
 */
function resolveCommonFields(input: BuildOrderRequestInput) {
  const {
    side,
    kind,
    qty,
    amount,
    leverage,
    marginType,
    hasOneWayPosition,
    tpSl,
    advancedTpSl,
    requestId,
    timestamp,
    refTimestamp,
  } = input;
  return {
    base: { side, requestId, timestamp, ...refTimestamp },
    orderTypeValue: kind === 'quantity' ? { qty } : { amount },
    tpSlValues: resolveTpSlExecutionFields({ submitSide: side, advancedPreset: advancedTpSl, basic: tpSl }),
    effectiveLeverage: hasOneWayPosition ? undefined : leverage,
    effectiveMarginType: hasOneWayPosition ? undefined : marginType,
  };
}

/**
 * Market 주문 본문(순수). 실행가는 Long→ask, Short→bid, 없으면 referencePrice.
 * slippage 필드는 market 전용(buildSlippageRequestFields가 market이 아니면 빈 객체 반환).
 */
export function buildMarketOrderRequest(input: BuildOrderRequestInput): MarketOrderPlan {
  const {
    side,
    tradeMode,
    leverage,
    marginType,
    reduceOnly,
    attribution,
    askPrice,
    bidPrice,
    referencePrice,
    slippage,
  } = input;
  const { base, orderTypeValue, tpSlValues, effectiveLeverage, effectiveMarginType } = resolveCommonFields(input);
  const price = (side === 'Long' ? askPrice : bidPrice) ?? referencePrice;
  const slippageFields = buildSlippageRequestFields(slippage, {
    priceType: 'market',
    referencePrice,
    side,
    bbo: price,
  });

  if (tradeMode === 'MultiplePositions') {
    return {
      variant: 'marketMulti',
      body: {
        ...base,
        leverage,
        price,
        ...tpSlValues,
        ...orderTypeValue,
        attribution,
        marginType,
        orderType: OrderTypeEnum['Market'],
        ...slippageFields,
      },
    };
  }
  return {
    variant: 'marketOneway',
    body: {
      ...base,
      reduceOnly,
      leverage: effectiveLeverage,
      price,
      ...orderTypeValue,
      attribution,
      marginType: effectiveMarginType,
      orderType: OrderTypeEnum['Market'],
      ...tpSlValues,
      ...slippageFields,
    },
  };
}

/** Limit 주문 본문(순수). BBO면 price 생략 + priceMatchStrategy, 아니면 명시 limitPrice(둘은 상호 배타). */
export function buildLimitOrderRequest(input: BuildOrderRequestInput): LimitOrderPlan {
  const { tradeMode, leverage, marginType, reduceOnly, attribution, isBbo, limitPrice } = input;
  const { base, orderTypeValue, tpSlValues, effectiveLeverage, effectiveMarginType } = resolveCommonFields(input);
  const priceFields = isBbo
    ? { price: undefined, priceMatchStrategy: PRICE_MATCH_STRATEGY_BBO }
    : { price: limitPrice, priceMatchStrategy: undefined };

  if (tradeMode === 'MultiplePositions') {
    return {
      variant: 'limitMulti',
      body: {
        ...base,
        leverage,
        ...tpSlValues,
        ...orderTypeValue,
        attribution,
        marginType,
        ...priceFields,
        orderType: OrderTypeEnum['Limit'],
      },
    };
  }
  return {
    variant: 'limitOneway',
    body: {
      ...base,
      reduceOnly,
      leverage: effectiveLeverage,
      ...orderTypeValue,
      attribution,
      marginType: effectiveMarginType,
      ...priceFields,
      orderType: OrderTypeEnum['Limit'],
      ...tpSlValues,
    },
  };
}

/**
 * Trigger 주문 본문(순수). Trigger Price 전용(Trigger Time은 sunset되어 생성 경로 없음, ERD-216 §11).
 * Trigger Limit: orderType='Limit' + (orderPrice 또는 priceMatchStrategy=BBO). Trigger Market: 필드 omit(백엔드 Market 처리).
 * triggerDirection: triggerPrice ≥ triggerReferencePrice → 'Up' else 'Down'.
 */
export function buildTriggerOrderRequest(input: BuildOrderRequestInput): TriggerOrderPlan {
  const {
    tradeMode,
    leverage,
    marginType,
    reduceOnly,
    attribution,
    triggerExecutionType,
    triggerPrice,
    // triggerPriceType, // ROLLBACK(last-price)
    triggerReferencePrice,
    isBbo,
    limitPrice,
  } = input;
  const { base, orderTypeValue, tpSlValues, effectiveLeverage, effectiveMarginType } = resolveCommonFields(input);
  const triggerLimitFields =
    triggerExecutionType === 'limit'
      ? {
          orderType: 'Limit' as const,
          ...(isBbo ? { priceMatchStrategy: PRICE_MATCH_STRATEGY_BBO } : { orderPrice: limitPrice }),
        }
      : {};
  const triggerDirection = Num(triggerPrice).isGreaterThanOrEqualTo(triggerReferencePrice) ? 'Up' : 'Down';

  if (tradeMode === 'MultiplePositions') {
    return {
      variant: 'triggerMulti',
      body: {
        action: 'Open',
        ...base,
        leverage,
        ...orderTypeValue,
        marginType,
        // ROLLBACK(last-price): 원본 — orders: [{ triggerPrice, triggerPriceType, triggerDirection, ...triggerLimitFields, ...tpSlValues }],
        orders: [{ triggerPrice, triggerDirection, ...triggerLimitFields, ...tpSlValues }],
        attribution,
      },
    };
  }
  return {
    variant: 'triggerOneway',
    body: {
      ...base,
      leverage: effectiveLeverage,
      ...orderTypeValue,
      reduceOnly,
      triggerPrice,
      // triggerPriceType, // ROLLBACK(last-price)
      triggerDirection,
      marginType: effectiveMarginType,
      attribution,
      ...triggerLimitFields,
      ...tpSlValues,
    },
  };
}

/**
 * 폼 상태 → 서버 요청 본문 변환(순수). priceType으로 per-order 빌더에 디스패치한다.
 * action.tsx `submitOrder`는 이 함수만 호출하고, 검증 가드·API 호출·토스트는 submitOrder가 소유한다.
 */
export function buildOrderRequest(input: BuildOrderRequestInput): OrderRequestPlan {
  switch (input.priceType) {
    case 'market':
      return buildMarketOrderRequest(input);
    case 'limit':
      return buildLimitOrderRequest(input);
    case 'trigger':
      return buildTriggerOrderRequest(input);
    default:
      input.priceType satisfies never;
      throw new Error(`Unexpected priceType: ${input.priceType}`);
  }
}
